<template>
  <div class="space-y-4">
    <!-- Date Range Display -->
    <div class="flex flex-col sm:flex-row gap-4">
      <!-- Start Date Display (Read-only) -->
      <div class="flex-1">
        <div class="bg-black/60 backdrop-blur-lg border border-white/20 rounded-2xl px-4 py-3">
          <div class="flex items-center justify-between">
            <div class="flex flex-col">
              <label class="text-xs font-medium text-gray-300 mb-1">Start Date</label>
              <span class="text-sm font-semibold text-white">{{ formatDateDisplay(startDate) }}</span>
            </div>
            <div class="w-8 h-8 bg-black/40 rounded-xl flex items-center justify-center">
              <svg class="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            </div>
          </div>
        </div>
      </div>
      
      <!-- End Date Display (Read-only) -->
      <div class="flex-1">
        <div class="bg-black/60 backdrop-blur-lg border border-white/20 rounded-2xl px-4 py-3">
          <div class="flex items-center justify-between">
            <div class="flex flex-col">
              <label class="text-xs font-medium text-gray-300 mb-1">End Date</label>
              <span class="text-sm font-semibold text-white">{{ formatDateDisplay(endDate) }}</span>
            </div>
            <div class="w-8 h-8 bg-black/40 rounded-xl flex items-center justify-center">
              <svg class="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Date Range Preset Buttons - Primary Selection Method -->
    <div class="flex flex-wrap gap-3 justify-center">
      <button
        v-for="preset in datePresets"
        :key="preset.label"
        @click="applyPreset(preset)"
        class="px-6 py-3 text-sm font-medium rounded-xl transition-all duration-200 transform hover:scale-105"
        :class="isPresetActive(preset) 
          ? 'bg-gradient-to-r from-blue-500 to-blue-600 text-white shadow-lg border border-white/20' 
          : 'bg-black/40 backdrop-blur-md text-gray-300 hover:shadow-md border border-white/10'"
      >
        <div class="flex items-center space-x-2">
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
          <span class="font-bold">{{ preset.label }}</span>
        </div>
      </button>
    </div>

    <!-- Current Selection Display (Read-only) -->
    <div v-if="startDate && endDate" class="p-4 bg-black/60 backdrop-blur-lg border border-white/20 rounded-3xl shadow-xl">
      <div class="flex items-center justify-between">
        <div class="flex items-center space-x-3">
          <div class="w-10 h-10 bg-black/40 rounded-xl flex items-center justify-center">
            <svg class="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
          </div>
          <div>
            <span class="text-sm font-semibold text-white">
              {{ formatDateRange(startDate, endDate) }}
            </span>
            <div class="text-xs text-gray-300 mt-1">
              {{ getDaysDifference(startDate, endDate) }} days selected
            </div>
          </div>
        </div>
        <div class="flex items-center space-x-2">
          <div class="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
          <span class="text-xs font-medium text-green-400">
            Current Range
          </span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'ModernDateRangeSelector',
  data() {
    // Get the current date
    const currentDate = new Date()
    
    // Calculate the start date of the current month
    const currentYear = currentDate.getFullYear()
    const currentMonth = currentDate.getMonth()
    const startOfMonth = new Date(currentYear, currentMonth, 1)

    return {
      startDate: startOfMonth,
      endDate: currentDate,
      datePresets: [
        { label: 'Today', getStart: () => new Date(), getEnd: () => new Date() },
        { label: 'Last 7 days', getStart: () => this.getDateOffset(-6), getEnd: () => new Date() },
        { label: 'Last 30 days', getStart: () => this.getDateOffset(-29), getEnd: () => new Date() },
        { label: 'This month', getStart: () => new Date(currentYear, currentMonth, 1), getEnd: () => new Date() },
        { label: 'Last month', getStart: () => new Date(currentYear, currentMonth - 1, 1), getEnd: () => new Date(currentYear, currentMonth, 0) }
      ]
    }
  },
  watch: {
    startDate: {
      handler(newVal, oldVal) {
        if (newVal && oldVal && newVal.getTime() !== oldVal.getTime()) {
          this.emitDateRangeChange()
        }
      },
      deep: true
    },
    endDate: {
      handler(newVal, oldVal) {
        if (newVal && oldVal && newVal.getTime() !== oldVal.getTime()) {
          this.emitDateRangeChange()
        }
      },
      deep: true
    }
  },
  methods: {
    handleStartDateChange(date) {
      console.log('Start date changed:', date)
      this.startDate = date
      // If end date is before start date, update end date
      if (this.endDate && date && this.endDate < date) {
        this.endDate = date
      }
      // Emit the change immediately
      this.emitDateRangeChange()
    },
    formatDateDisplay(date) {
      if (!date) return 'Select a date';
      
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    },
    applyPreset(preset) {
      console.log('Applying preset:', preset.label);
      this.startDate = preset.getStart();
      this.endDate = preset.getEnd();
      
      // Immediately emit the date range change
      this.emitDateRangeChange();
      
      // Force update to refresh the UI
      this.$forceUpdate();
    },
    isPresetActive(preset) {
      if (!this.startDate || !this.endDate) return false
      
      const presetStart = preset.getStart()
      const presetEnd = preset.getEnd()
      
      return this.isSameDay(this.startDate, presetStart) && 
             this.isSameDay(this.endDate, presetEnd)
    },
    getDateOffset(days) {
      const date = new Date()
      date.setDate(date.getDate() + days)
      return date
    },
    formatDateRange(start, end) {
      if (!start || !end) return ''
      
      const startFormatted = start.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      })
      
      const endFormatted = end.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      })
      
      return `${startFormatted} - ${endFormatted}`
    },
    getDaysDifference(start, end) {
      if (!start || !end) return 0
      
      const diffTime = Math.abs(end - start)
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
      return diffDays + 1 // Include both start and end dates
    },
    isSameDay(date1, date2) {
      return date1.getDate() === date2.getDate() &&
             date1.getMonth() === date2.getMonth() &&
             date1.getFullYear() === date2.getFullYear()
    },
    emitDateRangeChange() {
      if (this.startDate && this.endDate) {
        // Create a strongly-typed date range object for the API
        const dateRange = {
          startDate: this.formatDateForAPI(this.startDate),
          endDate: this.formatDateForAPI(this.endDate)
        };
        
        console.log('Emitting date range change:', dateRange);
        this.$emit('date-range-changed', dateRange);
        
        // Force the dashboard view to update
        this.$nextTick(() => {
          // Look for parent component that might be the dashboard
          let parent = this.$parent;
          while (parent) {
            if (parent.$forceUpdate) {
              parent.$forceUpdate();
            }
            parent = parent.$parent;
          }
        });
      }
    },
    formatDateForAPI(date) {
      return `${date.getFullYear()}-${(date.getMonth() + 1)
        .toString()
        .padStart(2, '0')}-${date.getDate().toString().padStart(2, '0')}`
    }
  },
  mounted() {
    // Emit initial date range
    this.emitDateRangeChange()
  }
}
</script> 